import React from "react";
import { Link } from "react-router-dom";
import { Package, Clock, FileText, Building2, Truck, Phone, MessageCircle, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { businessInfo, services } from "../data/mock";

const iconMap = { Package, Clock, FileText, Building2, Truck };

const additionalFeatures = [
  "Real-time shipment tracking",
  "Safe packaging & handling",
  "Competitive rates for businesses",
  "Multiple courier partner options",
  "Dedicated customer support",
  "Insurance on valuable shipments",
];

export default function ServicesPage() {
  return (
    <div>
      {/* Page Header */}
      <section className="bg-brand-dark py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold text-brand-light uppercase tracking-wider mb-2">Our Services</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: "'Lora', serif" }}>Courier & Logistics Services</h1>
          <p className="text-white/70 mt-3 max-w-2xl text-base">Complete courier solutions for individuals and businesses across India.</p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComp = iconMap[service.icon];
              return (
                <Card key={service.id} className="group bg-white hover:shadow-xl transition-shadow border border-gray-100 overflow-hidden">
                  <div className="h-1.5 bg-brand w-full" />
                  <CardContent className="p-6 pt-5">
                    <div className="w-14 h-14 rounded-lg bg-brand-lightest flex items-center justify-center mb-5 group-hover:bg-brand transition-colors">
                      {IconComp && <IconComp className="w-7 h-7 text-brand group-hover:text-white transition-colors" />}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3" style={{ fontFamily: "'Lora', serif" }}>{service.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold text-brand uppercase tracking-wider mb-2">Additional Features</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Lora', serif" }}>Everything You Need for Hassle-Free Shipping</h2>
              <div className="grid gap-4">
                {additionalFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand shrink-0" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg border">
              <h3 className="text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Lora', serif" }}>Need a Custom Solution?</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                For bulk shipments, regular business deliveries, or any special courier requirements, contact us for a customized solution at competitive rates.
              </p>
              <div className="flex flex-col gap-3">
                <Button asChild className="bg-brand hover:bg-brand-dark text-white font-semibold w-full">
                  <a href={`tel:${businessInfo.phone}`}>
                    <Phone className="w-4 h-4" />
                    Call Us – {businessInfo.phone}
                  </a>
                </Button>
                <Button asChild className="bg-[#25D366] hover:bg-[#1da851] text-white font-semibold w-full">
                  <a href={businessInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp Us
                  </a>
                </Button>
                <Button asChild variant="outline" className="border-brand text-brand hover:bg-brand hover:text-white font-semibold w-full">
                  <Link to="/contact">
                    Book a Pickup
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
